<template>
  <v-container
    id="one-on-one-manager"
    fluid
    tag="section"
    class="dashboard-container"
    style="padding: 24px 96px;"
  >
    <v-row class="mb-2 ma-n2" align="center">
      <v-col cols="12" sm="auto" class="pa-2">
        <div class="d-flex align-center">
          <v-icon color="#00E5FF" large class="mr-3">mdi-account-group</v-icon>
          <h1 class="text-h5 font-weight-bold text--primary">One-on-One (Gestão)</h1>
        </div>
      </v-col>
      <v-spacer />
      <v-col cols="12" sm="auto" class="pa-2">
        <v-btn
          v-if="!isViewOnlyMode"
          color="#00E5FF"
          height="40"
          depressed
          class="px-5 font-weight-bold action-btn black--text"
          style="border-radius: 8px;"
          @click="openScheduleDialog"
        >
          <v-icon left color="black">mdi-plus</v-icon>
          Agendar Reunião
        </v-btn>
      </v-col>
    </v-row>

    <v-row class="mb-4 ma-n2" align="center">
      <v-col cols="12" sm="auto" class="pa-2">
        <v-select
          v-model="selectedTeam"
          :items="teams"
          item-text="name"
          item-value="id"
          label="Todos os times"
          outlined
          dense
          hide-details
          prepend-inner-icon="mdi-account-multiple-outline"
          class="elevment-input"
          color="#00E5FF"
          style="min-width: 220px"
          clearable
          @change="onTeamChange"
        >
          <template v-slot:selection="{ item }">
            <span class="text-body-2 font-weight-medium">{{ item.name }}</span>
            <v-chip v-if="getTeamComplianceRate(item.id)" x-small color="rgba(0, 229, 255, 0.1)" text-color="#00E5FF" class="ml-2 font-weight-bold">
              {{ getTeamComplianceRate(item.id) }}%
            </v-chip>
          </template>
        </v-select>
      </v-col>

      <v-col cols="12" sm="auto" class="pa-2">
        <v-select
          v-model="selectedUser"
          :items="filteredUsers"
          item-text="name"
          item-value="id"
          label="Todos os colaboradores"
          outlined
          dense
          hide-details
          prepend-inner-icon="mdi-account-outline"
          class="elevment-input"
          color="#00E5FF"
          style="min-width: 260px"
          clearable
          @change="onFilterChange"
        >
          <template v-slot:item="{ item }">
            <v-list-item-avatar size="28">
              <v-avatar :color="item.avatarUrl ? 'transparent' : getAvatarColor(item.name)" size="28">
                <v-img v-if="item.avatarUrl" :src="item.avatarUrl" :alt="item.name" />
                <span v-else class="white--text" style="font-size: 11px">{{ getInitials(item.name) }}</span>
              </v-avatar>
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title class="text-body-2">{{ item.name }}</v-list-item-title>
            </v-list-item-content>
          </template>
          <template v-slot:selection="{ item }">
            <div class="d-flex align-center">
              <v-avatar :color="item.avatarUrl ? 'transparent' : getAvatarColor(item.name)" size="24" class="mr-2">
                <v-img v-if="item.avatarUrl" :src="item.avatarUrl" :alt="item.name" />
                <span v-else class="white--text" style="font-size: 9px">{{ getInitials(item.name) }}</span>
              </v-avatar>
              <span class="text-body-2 font-weight-medium">{{ item.name }}</span>
            </div>
          </template>
        </v-select>
      </v-col>
    </v-row>

    <v-card v-if="selectedTeam" class="elevment-card elevation-0 mb-5 pa-4">
      <div class="d-flex align-center justify-space-between flex-wrap" style="gap: 16px">
        <div class="d-flex align-center flex-grow-1" style="gap: 12px">
          <span class="font-weight-bold text-body-1 text--primary">Status do Time {{ getTeamName(selectedTeam) }}</span>
          <v-chip small color="rgba(0, 229, 255, 0.1)" text-color="#00E5FF" class="font-weight-bold">{{ teamStatus.complianceRate }}% em dia</v-chip>
        </div>
        <div class="d-flex align-center" style="gap: 16px">
          <div class="flex-grow-1" style="min-width: 200px; max-width: 400px">
            <v-progress-linear
              :value="teamStatus.completionPercent"
              color="#00E5FF"
              background-color="rgba(0, 229, 255, 0.1)"
              height="8"
              rounded
            />
          </div>
          <span class="text-caption text--secondary flex-shrink-0 font-weight-medium">
            {{ teamStatus.realized }} de {{ teamStatus.total }} realizadas
          </span>
        </div>

        <div v-if="teamStatus.pending.length > 0" class="d-flex align-center" style="gap: 8px">
          <div class="d-flex align-center">
            <v-icon small color="warning" class="mr-1">mdi-alert</v-icon>
            <span class="font-weight-bold text-body-2 text--primary">Pendentes ({{ teamStatus.pending.length }})</span>
          </div>
          <div v-for="p in teamStatus.pending" :key="p.userId" class="d-flex align-center ml-2">
            <v-avatar :color="p.avatarUrl ? 'transparent' : getAvatarColor(p.name)" size="24" class="mr-1">
              <v-img v-if="p.avatarUrl" :src="p.avatarUrl" :alt="p.name" />
              <span v-else class="white--text" style="font-size: 10px">{{ getInitials(p.name) }}</span>
            </v-avatar>
            <span class="text-body-2 font-weight-medium mr-1 text--primary">{{ p.name }}</span>
            <span class="text-caption warning--text">{{ p.daysSince }}d</span>
          </div>
        </div>
      </div>
    </v-card>

    <v-card v-if="selectedUser && !selectedTeam" class="elevment-card elevation-0 mb-5 pa-4">
      <div class="d-flex align-center justify-space-between flex-wrap" style="gap: 16px">
        <div class="d-flex align-center flex-grow-1" style="gap: 12px">
          <v-avatar :color="getSelectedUserData()?.avatarUrl ? 'transparent' : getAvatarColor(getSelectedUserData()?.name || '')" size="40">
            <v-img v-if="getSelectedUserData()?.avatarUrl" :src="getSelectedUserData().avatarUrl" :alt="getSelectedUserData().name" />
            <span v-else class="white--text" style="font-size: 14px">{{ getInitials(getSelectedUserData()?.name || '') }}</span>
          </v-avatar>
          <div>
            <span class="font-weight-bold text-h6 text--primary" style="line-height: 1.2;">{{ getSelectedUserData()?.name }}</span>
            <div class="text-caption text--secondary">Visualização individual</div>
          </div>
        </div>
        <v-btn
          small
          outlined
          class="action-btn-outlined font-weight-bold text-none"
          @click="selectedUser = null; onFilterChange()"
        >
          <v-icon small left>mdi-close</v-icon>
          Limpar filtro
        </v-btn>
      </div>
    </v-card>

    <v-row class="mb-5 ma-n2 top-cards-row">
      <v-col v-for="stat in statsCards" :key="stat.label" cols="12" sm="6" md="2" class="pa-2 d-flex">
        <v-card class="elevment-card elevation-0 top-card flex-grow-1">
          <v-card-text class="pa-4 d-flex flex-column justify-center align-start" style="height: 100%">
            <div class="d-flex align-center mb-2 w-100">
              <v-icon size="20" :color="stat.iconColor" class="mr-2">{{ stat.icon }}</v-icon>
              <span class="text-caption text--secondary">{{ stat.label }}</span>
            </div>
            <h3 class="text-h4 font-weight-bold" :style="{ color: stat.iconColor }">{{ stat.value }}</h3>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row class="mb-5 ma-n2">
      <v-col cols="12" md="6" class="pa-2">
        <v-card class="elevment-card elevation-0 fill-height">
          <v-card-text class="pa-5">
            <div class="d-flex align-center mb-4">
              <v-icon color="#00E5FF" class="mr-2">mdi-lightbulb-on-outline</v-icon>
              <h3 class="text-h6 font-weight-bold text--primary">{{ insightsTitle }}</h3>
            </div>

            <div
              v-for="(insight, idx) in insights"
              :key="idx"
              class="insight-item pa-4 mb-3 rounded-lg elevment-box"
              :class="'insight-item--' + insight.type"
            >
              <div class="d-flex">
                <v-avatar :color="getInsightBgColor(insight.type)" size="36" class="mr-3 flex-shrink-0">
                  <v-icon :color="getInsightIconColor(insight.type)" size="18">{{ insight.icon }}</v-icon>
                </v-avatar>
                <div>
                  <h4 class="text-subtitle-2 font-weight-bold mb-1 text--primary">{{ insight.title }}</h4>
                  <p class="text-body-2 text--secondary mb-0" style="line-height: 1.4;">{{ insight.description }}</p>
                  <a
                    v-if="insight.actionLabel"
                    href="#"
                    class="text-caption font-weight-bold mt-2 d-inline-flex align-center"
                    style="color: #00E5FF; text-decoration: none"
                    @click.prevent="handleInsightAction(insight)"
                  >
                    {{ insight.actionLabel }}
                    <v-icon x-small color="#00E5FF" class="ml-1">mdi-arrow-right</v-icon>
                  </a>
                </div>
              </div>
            </div>

            <div v-if="!insights || insights.length === 0" class="text-center py-8">
              <v-icon size="48" color="rgba(128,128,128,0.2)">mdi-lightbulb-outline</v-icon>
              <p class="text-body-2 text--secondary mt-3 mb-0">Nenhum insight disponível no momento</p>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="6" class="pa-2">
        <v-card class="elevment-card elevation-0 fill-height">
          <v-card-text class="pa-5">
            <div class="d-flex align-center justify-space-between mb-4">
              <div class="d-flex align-center">
                <v-icon color="#00E5FF" class="mr-2">mdi-account-multiple</v-icon>
                <h3 class="text-h6 font-weight-bold text--primary">Colaboradores frequentes</h3>
              </div>
              <v-chip v-if="selectedUser" x-small class="theme-badge cyan--text" outlined>
                Filtrado
              </v-chip>
            </div>

            <div v-if="filteredCollaborators.length > 0">
              <div
                v-for="collab in filteredCollaborators"
                :key="collab.userId"
                class="mb-4"
              >
                <div class="d-flex align-center justify-space-between mb-2">
                  <div class="d-flex align-center">
                    <v-avatar :color="collab.avatarUrl ? 'transparent' : getAvatarColor(collab.name)" size="32" class="mr-3">
                      <v-img v-if="collab.avatarUrl" :src="collab.avatarUrl" :alt="collab.name" />
                      <span v-else class="white--text" style="font-size: 11px">{{ getInitials(collab.name) }}</span>
                    </v-avatar>
                    <span class="text-body-2 font-weight-bold text--primary mr-2">{{ collab.name }}</span>
                    <v-chip
                      v-if="collab.changeThisMonth !== 0"
                      x-small
                      :class="collab.changeThisMonth > 0 ? 'theme-badge cyan--text' : 'theme-badge warning--text'"
                      outlined
                    >
                      {{ collab.changeThisMonth > 0 ? '+' : '' }}{{ collab.changeThisMonth }} este mês
                    </v-chip>
                    <v-chip v-else x-small outlined class="theme-badge grey--text">0 este mês</v-chip>
                  </div>
                  <span class="text-body-2 font-weight-bold text--primary">{{ collab.totalMeetings }} reuniões</span>
                </div>
                <v-progress-linear
                  :value="(collab.totalMeetings / maxCollabMeetings) * 100"
                  color="#00E5FF"
                  background-color="rgba(0, 229, 255, 0.1)"
                  height="6"
                  rounded
                />
              </div>

              <v-btn
                v-if="!selectedUser"
                block
                outlined
                class="action-btn-outlined font-weight-bold text-none mt-4"
                style="border-radius: 8px;"
              >
                Ver histórico completo
              </v-btn>
            </div>

            <div v-else class="text-center py-8">
              <v-icon size="48" color="rgba(128,128,128,0.2)">mdi-account-multiple-outline</v-icon>
              <p class="text-body-2 text--secondary mt-3 mb-0">Nenhum dado disponível</p>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-card class="elevment-card elevation-0">
      <v-card-text class="pa-5">
        <div class="d-flex align-center justify-space-between flex-wrap mb-4" style="gap: 12px">
          <h3 class="text-h5 font-weight-bold text--primary">Reuniões</h3>

          <div class="d-flex align-center" style="gap: 12px">
            <v-text-field
              v-model="meetingSearch"
              placeholder="Buscar reuniões..."
              outlined
              dense
              hide-details
              prepend-inner-icon="mdi-magnify"
              style="max-width: 260px"
              class="elevment-input"
              color="#00E5FF"
              clearable
            />
          </div>
        </div>

        <v-tabs
          v-model="meetingTab"
          background-color="transparent"
          color="#00E5FF"
          slider-color="#00E5FF"
          class="mb-4 custom-tabs"
        >
          <v-tab class="text-none font-weight-bold px-4">
            <v-icon left small>mdi-calendar</v-icon>
            Próximas
            <v-chip v-if="filteredUpcomingMeetings.length > 0" x-small color="rgba(0, 229, 255, 0.1)" text-color="#00E5FF" class="ml-2 font-weight-bold">
              {{ filteredUpcomingMeetings.length }}
            </v-chip>
          </v-tab>
          <v-tab class="text-none font-weight-bold px-4">
            <v-icon left small>mdi-history</v-icon>
            Anteriores
            <v-chip v-if="filteredPastMeetings.length > 0" x-small outlined class="theme-badge grey--text ml-2">
              {{ filteredPastMeetings.length }}
            </v-chip>
          </v-tab>
        </v-tabs>

        <v-divider class="theme-divider mt-n4 mb-4" />

        <v-tabs-items v-model="meetingTab" style="background: transparent;">
          <v-tab-item>
            <div v-if="loading" class="text-center py-10">
              <v-progress-circular indeterminate color="#00E5FF" size="48" width="3" />
              <p class="mt-4 text-body-2 text--secondary">Carregando reuniões...</p>
            </div>

            <div v-else-if="filteredUpcomingMeetings.length > 0">
              <v-card
                v-for="meeting in filteredUpcomingMeetings"
                :key="meeting.id"
                class="elevment-card elevation-0 mb-3 meeting-list-card"
              >
                <div class="d-flex pa-4 align-center flex-wrap">
                  <v-avatar :color="meeting.userAvatar ? 'transparent' : getAvatarColor(meeting.userName)" size="48" class="mr-4 flex-shrink-0">
                    <v-img v-if="meeting.userAvatar" :src="meeting.userAvatar" :alt="meeting.userName" />
                    <span v-else class="white--text font-weight-bold text-subtitle-1">{{ getInitials(meeting.userName) }}</span>
                  </v-avatar>

                  <div class="flex-grow-1" style="overflow-wrap: break-word; word-wrap: break-word; min-width: 250px;">
                    <div class="mb-1">
                      <span class="font-weight-bold text-subtitle-1 text--primary" style="word-break: break-word; line-height: 1.2;">
                        {{ meeting.userName }}
                      </span>
                    </div>
                    <div class="text-caption text--secondary" style="word-break: break-word;">{{ meeting.userJobRole || 'Cargo não informado' }}</div>

                    <div class="d-flex flex-wrap mt-2" style="gap: 6px">
                      <v-chip
                        v-for="topic in (meeting.topics || []).slice(0, 3)"
                        :key="topic"
                        x-small
                        outlined
                        class="theme-chip-topic"
                      >
                        {{ topic }}
                      </v-chip>
                    </div>

                    <div class="d-flex flex-wrap align-center mt-3" style="gap: 8px">
                      <v-btn
                        v-if="!isViewOnlyMode && !meeting.occurredAt"
                        small
                        depressed
                        :color="(meeting.meetingUrl || meeting.joinWebUrl) ? '#6264A7' : '#00E5FF'"
                        :class="{'black--text': !(meeting.meetingUrl || meeting.joinWebUrl), 'white--text': (meeting.meetingUrl || meeting.joinWebUrl)}"
                        class="font-weight-bold action-btn text-none"
                        style="border-radius: 6px;"
                        @click="startMeeting(meeting)"
                      >
                        <v-icon left small :color="(meeting.meetingUrl || meeting.joinWebUrl) ? 'white' : 'black'">
                          {{ (meeting.meetingUrl || meeting.joinWebUrl) ? 'mdi-microsoft-teams' : 'mdi-video-outline' }}
                        </v-icon>
                        {{ (meeting.meetingUrl || meeting.joinWebUrl) ? 'Entrar no Teams' : 'Iniciar' }}
                      </v-btn>
                      
                      <v-chip
                        v-if="meeting.occurredAt"
                        small
                        outlined
                        class="theme-badge success--text"
                      >
                        <v-icon left x-small>mdi-check-circle</v-icon>
                        Realizada
                      </v-chip>

                      <v-btn
                        small
                        outlined
                        class="action-btn-outlined font-weight-bold text-none"
                        style="border-radius: 6px;"
                        @click="preparePauta(meeting)"
                      >
                        <v-icon left small>mdi-note-text-outline</v-icon>
                        Pauta
                      </v-btn>

                      <v-btn
                        v-if="!isViewOnlyMode"
                        small
                        outlined
                        class="action-btn-outlined font-weight-bold text-none"
                        style="border-radius: 6px;"
                        @click="reschedule(meeting)"
                      >
                        <v-icon left small>mdi-calendar-sync</v-icon>
                        Reagendar
                      </v-btn>

                      <v-btn
                        v-if="!isViewOnlyMode && isMeetingToday(meeting) && !meeting.occurredAt"
                        small
                        outlined
                        class="action-btn-outlined font-weight-bold text-none success--text"
                        style="border-radius: 6px; border-color: #34C759 !important;"
                        @click="openOccurredConfirmDialog(meeting)"
                      >
                        <v-icon left small color="success">mdi-check-circle-outline</v-icon>
                        Marcar realizada
                      </v-btn>

                      <v-menu v-if="!isViewOnlyMode" bottom left offset-y>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn icon small v-bind="attrs" v-on="on">
                            <v-icon size="20" color="grey">mdi-dots-horizontal</v-icon>
                          </v-btn>
                        </template>
                        <v-list dense class="elevment-menu">
                          <v-list-item @click="openCancelDialog(meeting)">
                            <v-list-item-icon class="mr-2">
                              <v-icon small color="error">mdi-cancel</v-icon>
                            </v-list-item-icon>
                            <v-list-item-title class="error--text text-body-2 font-weight-medium">Cancelar reunião</v-list-item-title>
                          </v-list-item>
                        </v-list>
                      </v-menu>
                    </div>
                  </div>

                  <div class="text-right flex-shrink-0 ml-auto mt-2 mt-sm-0" style="min-width: 140px;">
                    <v-chip
                      outlined
                      x-small
                      class="elevation-0 theme-badge mb-2"
                      :class="meeting.role === 'Mentor' ? 'cyan--text' : 'warning--text'"
                    >
                      {{ meeting.role || 'Mentor' }}
                    </v-chip>
                    <div class="text-subtitle-2 font-weight-bold text--primary">{{ formatMeetingDateShort(meeting.scheduledAt) }}</div>
                    <div class="d-flex align-center justify-end mb-2">
                      <v-icon x-small class="mr-1" color="grey">mdi-clock-outline</v-icon>
                      <span class="text-caption text--secondary">{{ formatMeetingTime(meeting.scheduledAt) }} · {{ meeting.duration || 45 }}min</span>
                    </div>
                    
                    <v-btn icon small color="grey" class="expand-btn" @click="toggleMeetingExpand(meeting)">
                      <v-icon>{{ meeting.expanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                    </v-btn>
                  </div>
                </div>

                <v-expand-transition>
                  <div v-show="meeting.expanded">
                    <v-divider class="theme-divider" />
                    <v-card-text class="pa-5 expanded-area" :style="{ backgroundColor: $vuetify.theme.dark ? '#121212' : '#F5F5F7' }">
                      <v-row>
                        <div class="d-flex w-100 flex-wrap" style="gap: 24px">
                          <div class="flex-grow-1">
                            <h4 class="text-caption text-uppercase font-weight-bold text--primary mb-2">Pauta</h4>
                            <p class="text-body-2 text--secondary" style="line-height: 1.5;">{{ meeting.notes || 'Nenhuma pauta definida ainda.' }}</p>
                          </div>
                          <div style="min-width: 150px">
                            <h4 class="text-caption text-uppercase font-weight-bold text--primary mb-2">Histórico</h4>
                            <p class="text-body-2 text--secondary">{{ meeting.previousMeetings || 0 }} reuniões anteriores</p>
                          </div>
                        </div>
                      </v-row>
                    </v-card-text>
                  </div>
                </v-expand-transition>
              </v-card>
            </div>

            <v-card v-else class="pa-10 text-center elevment-card elevation-0">
              <v-icon size="64" color="rgba(128,128,128,0.2)">mdi-calendar-blank</v-icon>
              <h3 class="text-h6 text--primary mt-5 font-weight-bold">Nenhuma reunião agendada</h3>
              <p class="text-body-2 text--secondary mb-5">Comece agendando sua primeira reunião One-on-One</p>
              <v-btn
                v-if="!isViewOnlyMode"
                color="#00E5FF"
                depressed
                class="font-weight-bold black--text"
                style="border-radius: 8px;"
                @click="openScheduleDialog"
              >
                <v-icon left color="black">mdi-plus</v-icon> Agendar Primeira Reunião
              </v-btn>
            </v-card>
          </v-tab-item>

          <v-tab-item>
            <div v-if="filteredPastMeetings.length > 0">
              <v-card
                v-for="meeting in filteredPastMeetings"
                :key="meeting.id"
                class="elevment-card elevation-0 mb-3 meeting-list-card meeting-card--past"
              >
                <div class="d-flex pa-4 align-center flex-wrap">
                  <v-avatar :color="meeting.userAvatar ? 'transparent' : getAvatarColor(meeting.userName)" size="48" class="mr-4 flex-shrink-0" style="opacity: 0.8">
                    <v-img v-if="meeting.userAvatar" :src="meeting.userAvatar" :alt="meeting.userName" />
                    <span v-else class="white--text font-weight-bold text-subtitle-1">{{ getInitials(meeting.userName) }}</span>
                  </v-avatar>

                  <div class="flex-grow-1" style="overflow-wrap: break-word; word-wrap: break-word; min-width: 250px;">
                    <div class="font-weight-bold text-subtitle-1 text--primary" style="word-break: break-word; line-height: 1.2;">{{ meeting.userName }}</div>
                    <div class="text-caption text--secondary" style="word-break: break-word;">{{ meeting.userJobRole }}</div>
                    
                    <div class="d-flex flex-wrap mt-2" style="gap: 6px">
                      <v-chip
                        v-for="topic in (meeting.topics || []).slice(0, 3)"
                        :key="topic"
                        x-small
                        outlined
                        class="theme-chip-topic"
                      >
                        {{ topic }}
                      </v-chip>
                    </div>
                    
                    <div class="d-flex align-center mt-3" style="gap: 8px">
                      <v-btn
                        small
                        outlined
                        class="action-btn-outlined font-weight-bold text-none"
                        style="border-radius: 6px;"
                        @click="viewNotes(meeting)"
                      >
                        <v-icon left small>mdi-eye-outline</v-icon>
                        Anotações
                      </v-btn>
                      <v-btn
                        v-if="!isViewOnlyMode && !meeting.occurredAt && meeting.status !== 'CANCELLED'"
                        small
                        outlined
                        class="action-btn-outlined font-weight-bold text-none success--text"
                        style="border-radius: 6px; border-color: #34C759 !important;"
                        @click="markAsOccurred(meeting)"
                      >
                        <v-icon left small color="success">mdi-check-circle-outline</v-icon>
                        Marcar realizada
                      </v-btn>
                    </div>
                  </div>

                  <div class="text-right flex-shrink-0 ml-auto mt-2 mt-sm-0" style="min-width: 140px;">
                    <div class="text-subtitle-2 font-weight-bold text--primary">{{ formatMeetingDateShort(meeting.scheduledAt) }}</div>
                    <div class="d-flex align-center justify-end">
                      <v-icon x-small class="mr-1" color="grey">mdi-clock-outline</v-icon>
                      <span class="text-caption text--secondary">{{ formatMeetingTime(meeting.scheduledAt) }} · {{ meeting.duration || 45 }}min</span>
                    </div>
                    
                    <v-chip
                      v-if="meeting.status === 'CANCELLED'"
                      outlined
                      x-small
                      class="elevation-0 theme-badge grey--text mt-2"
                    >
                      <v-icon left x-small>mdi-cancel</v-icon> Cancelada
                    </v-chip>
                    <v-chip
                      v-else-if="meeting.occurredAt || meeting.status === 'COMPLETED'"
                      outlined
                      x-small
                      class="elevation-0 theme-badge success--text mt-2"
                    >
                      <v-icon left x-small>mdi-check-circle</v-icon> Concluída
                    </v-chip>
                    <v-chip
                      v-else
                      outlined
                      x-small
                      class="elevation-0 theme-badge error--text mt-2"
                    >
                      <v-icon left x-small>mdi-close-circle-outline</v-icon> Não realizada
                    </v-chip>
                  </div>
                </div>
              </v-card>
            </div>

            <v-card v-else class="pa-10 text-center elevment-card elevation-0">
              <v-icon size="64" color="rgba(128,128,128,0.2)">mdi-history</v-icon>
              <h3 class="text-h6 text--primary mt-5 font-weight-bold">Nenhuma reunião anterior</h3>
            </v-card>
          </v-tab-item>
        </v-tabs-items>
      </v-card-text>
    </v-card>

    <v-dialog v-model="scheduleDialog" max-width="600px" persistent>
      <v-card class="elevment-dialog-card elevation-0">
        <v-card-title class="pa-6 pb-4 d-flex justify-space-between align-center">
          <div class="d-flex align-center">
            <v-icon color="#00E5FF" class="mr-3">mdi-calendar-text</v-icon>
            <div>
              <h3 class="text-h5 font-weight-bold text--primary mb-0" style="line-height: 1.2;">Agendar One-on-One</h3>
              <div class="text-caption text--secondary">Configure os detalhes da reunião</div>
            </div>
          </div>
          <v-btn icon color="grey" @click="closeScheduleDialog" small><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        
        <v-divider class="theme-divider" />

        <v-card-text class="pa-6" style="max-height: 65vh; overflow-y: auto">
          <div v-if="selectedMeetingToReschedule" class="mb-5 pa-4 elevment-box">
            <div class="d-flex align-center mb-2">
              <v-icon small color="#00E5FF" class="mr-2">mdi-information-outline</v-icon>
              <span class="text-caption font-weight-bold text-uppercase cyan--text" style="letter-spacing: 0.5px">
                Informações da Reunião
              </span>
            </div>
            <div v-if="selectedMeetingToReschedule.title" class="mb-2">
              <span class="text-caption text--secondary">Título:</span>
              <div class="text-body-2 font-weight-bold text--primary">{{ selectedMeetingToReschedule.title }}</div>
            </div>
            <div v-if="selectedMeetingToReschedule.notes">
              <span class="text-caption text--secondary">Anotações:</span>
              <div class="text-body-2 text--primary">{{ selectedMeetingToReschedule.notes }}</div>
            </div>
          </div>

          <v-form ref="form" v-model="validForm">
            <div class="text-subtitle-2 font-weight-bold text--primary mb-2">Tipo de reunião</div>
            <div class="d-flex mb-5" style="gap: 10px">
              <v-btn
                :color="newMeeting.meetingType === 'mentor' ? '#00E5FF' : 'transparent'"
                :class="newMeeting.meetingType === 'mentor' ? 'black--text font-weight-bold' : 'text--secondary font-weight-bold'"
                :outlined="newMeeting.meetingType !== 'mentor'"
                :style="newMeeting.meetingType !== 'mentor' ? 'border-color: rgba(128,128,128,0.3) !important;' : ''"
                :disabled="isReschedulingMode"
                depressed
                style="border-radius: 8px; flex: 1;"
                class="text-none"
                @click="newMeeting.meetingType = 'mentor'"
              >
                Você como Mentor
              </v-btn>
              <v-btn
                :color="newMeeting.meetingType === 'mentorado' ? '#00E5FF' : 'transparent'"
                :class="newMeeting.meetingType === 'mentorado' ? 'black--text font-weight-bold' : 'text--secondary font-weight-bold'"
                :outlined="newMeeting.meetingType !== 'mentorado'"
                :style="newMeeting.meetingType !== 'mentorado' ? 'border-color: rgba(128,128,128,0.3) !important;' : ''"
                :disabled="isReschedulingMode"
                depressed
                style="border-radius: 8px; flex: 1;"
                class="text-none"
                @click="newMeeting.meetingType = 'mentorado'"
              >
                Você como Mentorado
              </v-btn>
            </div>

            <div class="text-subtitle-2 font-weight-bold text--primary mb-2">Participante</div>
            <v-autocomplete
              v-model="newMeeting.userId"
              :items="filteredUsersForMeeting"
              item-text="name"
              item-value="id"
              placeholder="Selecione um colaborador"
              :rules="[v => !!v || 'Participante é obrigatório']"
              :disabled="isReschedulingMode"
              outlined
              dense
              hide-details="auto"
              class="mb-5 elevment-input"
              color="#00E5FF"
            >
              <template v-slot:item="{ item }">
                <v-list-item-avatar size="30">
                  <v-avatar :color="item.avatarUrl ? 'transparent' : getAvatarColor(item.name)" size="30">
                    <v-img v-if="item.avatarUrl" :src="item.avatarUrl" :alt="item.name" />
                    <span v-else class="white--text" style="font-size: 10px">{{ getInitials(item.name) }}</span>
                  </v-avatar>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title class="text-body-2">{{ item.name }}</v-list-item-title>
                  <v-list-item-subtitle class="text-caption">{{ item.jobRoleName || translateRole(item.role) }}</v-list-item-subtitle>
                </v-list-item-content>
              </template>
              <template v-slot:selection="{ item }">
                <div class="d-flex align-center">
                  <v-avatar :color="item.avatarUrl ? 'transparent' : getAvatarColor(item.name)" size="24" class="mr-2">
                    <v-img v-if="item.avatarUrl" :src="item.avatarUrl" :alt="item.name" />
                    <span v-else class="white--text" style="font-size: 9px">{{ getInitials(item.name) }}</span>
                  </v-avatar>
                  <span class="font-weight-medium">{{ item.name }}</span>
                </div>
              </template>
            </v-autocomplete>

            <v-row dense class="mb-5">
              <v-col cols="4">
                <div class="text-subtitle-2 font-weight-bold text--primary mb-2">Data</div>
                <v-text-field
                  v-model="newMeeting.date"
                  type="date"
                  :rules="[v => !!v || 'Obrigatória']"
                  outlined
                  dense
                  hide-details="auto"
                  prepend-inner-icon="mdi-calendar"
                  class="elevment-input"
                  color="#00E5FF"
                />
              </v-col>
              <v-col cols="4">
                <div class="text-subtitle-2 font-weight-bold text--primary mb-2">Horário</div>
                <v-select
                  v-model="newMeeting.time"
                  :items="timeOptions"
                  :rules="[v => !!v || 'Obrigatório']"
                  outlined
                  dense
                  hide-details="auto"
                  class="elevment-input"
                  color="#00E5FF"
                />
              </v-col>
              <v-col cols="4">
                <div class="text-subtitle-2 font-weight-bold text--primary mb-2">Duração</div>
                <v-select
                  v-model="newMeeting.duration"
                  :items="durationOptions"
                  outlined
                  dense
                  hide-details="auto"
                  prepend-inner-icon="mdi-clock-outline"
                  class="elevment-input"
                  color="#00E5FF"
                />
              </v-col>
            </v-row>

            <div class="d-flex align-center justify-space-between mb-2">
              <div class="text-subtitle-2 font-weight-bold text--primary">Tópicos da pauta</div>
              <v-btn
                x-small
                text
                class="text-none font-weight-bold"
                style="border-radius: 6px;"
                :color="showSuggestions ? '#00E5FF' : 'grey'"
                @click="showSuggestions = !showSuggestions"
              >
                <v-icon x-small left>mdi-auto-fix</v-icon>
                Sugestões
              </v-btn>
            </div>

            <div v-if="showSuggestions" class="d-flex flex-wrap mb-4" style="gap: 6px">
              <v-chip
                v-for="topic in suggestedTopics"
                :key="topic"
                :class="newMeeting.selectedTopics.includes(topic) ? 'theme-chip-filter active' : 'theme-chip-filter'"
                small
                outlined
                style="cursor: pointer; text-transform: none; font-size: 12px"
                @click="toggleTopic(topic)"
              >
                {{ topic }}
              </v-chip>
            </div>

            <div class="d-flex align-center mb-5" style="gap: 8px">
              <v-text-field
                v-model="customTopic"
                placeholder="Adicionar tópico personalizado..."
                outlined
                dense
                hide-details
                class="flex-grow-1 elevment-input"
                color="#00E5FF"
                @keydown.enter.prevent="addCustomTopic"
              />
              <v-btn
                icon
                small
                color="#00E5FF"
                class="action-btn-outlined"
                style="border-radius: 8px; width: 38px; height: 38px;"
                :disabled="!customTopic.trim()"
                @click="addCustomTopic"
              >
                <v-icon size="20">mdi-plus</v-icon>
              </v-btn>
            </div>

            <div v-if="newMeeting.customTopics.length" class="d-flex flex-wrap mb-5" style="gap: 6px; margin-top: -12px">
              <v-chip
                v-for="(topic, i) in newMeeting.customTopics"
                :key="'custom-' + i"
                small
                close
                class="theme-chip-filter active"
                @click:close="removeCustomTopic(i)"
              >
                {{ topic }}
              </v-chip>
            </div>

            <div class="text-subtitle-2 font-weight-bold text--primary mb-2">Notas prévias (opcional)</div>
            <v-textarea
              v-model="newMeeting.notes"
              placeholder="Adicione contexto ou pontos importantes para discutir..."
              outlined
              rows="3"
              hide-details="auto"
              class="elevment-input"
              color="#00E5FF"
            />
          </v-form>
        </v-card-text>

        <v-divider class="theme-divider" />

        <v-card-actions class="pa-6">
          <v-spacer />
          <v-btn
            text
            height="40"
            class="px-5 font-weight-bold text--secondary"
            style="border-radius: 8px;"
            @click="closeScheduleDialog"
          >
            Cancelar
          </v-btn>
          <v-btn
            color="#00E5FF"
            depressed
            height="40"
            class="px-6 font-weight-bold black--text ml-3"
            style="border-radius: 8px;"
            :loading="saving"
            :disabled="!validForm || isViewOnlyMode"
            @click="scheduleMeeting"
          >
            <v-icon left size="18" color="black">mdi-calendar-check</v-icon>
            Agendar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="notesDialog" max-width="600px">
      <v-card class="elevment-dialog-card elevation-0">
        <v-card-title class="pa-6 pb-4 d-flex justify-space-between align-center">
          <div>
            <h3 class="text-h5 font-weight-bold text--primary mb-1">Preparar Pauta</h3>
            <p class="text-body-2 text--secondary mb-0" v-if="selectedMeeting">
              {{ selectedMeeting.userName }} · {{ formatMeetingDateFull(selectedMeeting.scheduledAt) }}
            </p>
          </div>
          <v-btn icon color="grey" @click="closeNotesDialog" small><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        
        <v-divider class="theme-divider" />

        <v-card-text class="pa-6" v-if="selectedMeeting">
          <div class="mb-4">
            <div class="d-flex align-center justify-space-between mb-2">
              <div class="text-subtitle-2 font-weight-bold text--primary">Tópicos da pauta</div>
              <v-btn
                x-small
                text
                class="text-none font-weight-bold"
                style="border-radius: 6px;"
                :color="showPautaSuggestions ? '#00E5FF' : 'grey'"
                @click="showPautaSuggestions = !showPautaSuggestions"
              >
                <v-icon x-small left>mdi-auto-fix</v-icon>
                Sugestões
              </v-btn>
            </div>

            <div v-if="showPautaSuggestions" class="d-flex flex-wrap mb-4" style="gap: 6px">
              <v-chip
                v-for="topic in suggestedTopics"
                :key="topic"
                :class="pautaSelectedTopics.includes(topic) ? 'theme-chip-filter active' : 'theme-chip-filter'"
                small
                outlined
                style="cursor: pointer; text-transform: none; font-size: 12px"
                @click="togglePautaTopic(topic)"
              >
                {{ topic }}
              </v-chip>
            </div>

            <div class="d-flex align-center mb-4" style="gap: 8px">
              <v-text-field
                v-model="customPautaTopic"
                placeholder="Adicionar tópico personalizado..."
                outlined
                dense
                hide-details
                class="flex-grow-1 elevment-input"
                color="#00E5FF"
                @keydown.enter.prevent="addCustomPautaTopic"
              />
              <v-btn
                icon
                small
                color="#00E5FF"
                class="action-btn-outlined"
                style="border-radius: 8px; width: 38px; height: 38px;"
                :disabled="!customPautaTopic.trim()"
                @click="addCustomPautaTopic"
              >
                <v-icon size="20">mdi-plus</v-icon>
              </v-btn>
            </div>

            <div v-if="pautaCustomTopics.length" class="d-flex flex-wrap mb-4" style="gap: 6px">
              <v-chip
                v-for="(topic, i) in pautaCustomTopics"
                :key="'custom-' + i"
                small
                close
                class="theme-chip-filter active"
                @click:close="removeCustomPautaTopic(i)"
              >
                {{ topic }}
              </v-chip>
            </div>
          </div>

          <div v-if="selectedMeeting.notes" class="pa-4 elevment-box mb-3">
            <h4 class="text-subtitle-2 font-weight-bold mb-2 text--primary">Anotações Anteriores</h4>
            <p class="text-body-2 text--secondary mb-0" style="line-height: 1.5;">
              {{ selectedMeeting.notes }}
            </p>
          </div>
        </v-card-text>
        
        <v-divider class="theme-divider" />

        <v-card-actions class="pa-6">
          <v-spacer />
          <v-btn text height="40" class="px-5 font-weight-bold text--secondary" style="border-radius: 8px;" @click="closeNotesDialog">Cancelar</v-btn>
          <v-btn
            color="#00E5FF"
            depressed
            height="40"
            class="px-6 font-weight-bold black--text ml-3"
            style="border-radius: 8px;"
            :loading="saving"
            @click="savePautaTopics"
          >
            <v-icon left size="18" color="black">mdi-check</v-icon>
            Salvar Pauta
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="occurredConfirmDialog" max-width="420px" persistent>
      <v-card class="elevment-dialog-card elevation-0">
        <v-card-title class="pa-6 pb-4 d-flex align-start">
          <v-icon color="success" size="28" class="mr-4 mt-1">mdi-check-circle-outline</v-icon>
          <div>
            <h3 class="text-h5 font-weight-bold text--primary mb-1">Confirmar realização</h3>
            <p class="text-caption text--secondary mb-0">Esta ação não pode ser desfeita</p>
          </div>
        </v-card-title>
        <v-divider class="theme-divider" />
        <v-card-text class="pa-6">
          <p class="text-body-1 mb-2 text--primary">
            Confirmar que a reunião com
            <strong>{{ meetingToMarkOccurred && meetingToMarkOccurred.userName }}</strong>
            foi realizada?
          </p>
          <p class="text-caption text--secondary mb-0">
            A data de realização será registrada automaticamente pelo sistema.
          </p>
        </v-card-text>
        <v-divider class="theme-divider" />
        <v-card-actions class="pa-6">
          <v-spacer />
          <v-btn
            text
            height="40"
            class="px-5 font-weight-bold text--secondary"
            style="border-radius: 8px;"
            :disabled="markingOccurred"
            @click="occurredConfirmDialog = false"
          >
            Cancelar
          </v-btn>
          <v-btn
            depressed
            color="success"
            height="40"
            class="px-6 font-weight-bold white--text ml-3"
            style="border-radius: 8px;"
            :loading="markingOccurred"
            @click="confirmMarkAsOccurred"
          >
            <v-icon left small>mdi-check</v-icon>
            Sim, foi realizada
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="cancelDialog" max-width="420px" persistent>
      <v-card class="elevment-dialog-card elevation-0">
        <v-card-title class="pa-6 pb-4 d-flex align-start">
          <v-icon color="error" size="28" class="mr-4 mt-1">mdi-cancel</v-icon>
          <div>
            <h3 class="text-h5 font-weight-bold text--primary mb-1">Cancelar reunião</h3>
            <p class="text-caption text--secondary mb-0">Esta ação não pode ser desfeita</p>
          </div>
        </v-card-title>
        <v-divider class="theme-divider" />
        <v-card-text class="pa-6">
          <p class="text-body-1 mb-4 text--primary">
            Tem certeza que deseja cancelar a reunião com
            <strong>{{ meetingToCancel && meetingToCancel.userName }}</strong>?
          </p>
          <v-textarea
            v-model="cancelReason"
            placeholder="Motivo do cancelamento (opcional)"
            outlined
            dense
            rows="3"
            hide-details
            class="elevment-input"
            color="error"
          />
        </v-card-text>
        <v-divider class="theme-divider" />
        <v-card-actions class="pa-6">
          <v-spacer />
          <v-btn
            text
            height="40"
            class="px-5 font-weight-bold text--secondary"
            style="border-radius: 8px;"
            :disabled="cancelling"
            @click="cancelDialog = false"
          >
            Voltar
          </v-btn>
          <v-btn
            depressed
            color="error"
            height="40"
            class="px-6 font-weight-bold white--text ml-3"
            style="border-radius: 8px;"
            :loading="cancelling"
            @click="confirmCancelMeeting"
          >
            <v-icon left small>mdi-cancel</v-icon>
            Sim, cancelar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="3000" top elevation="0" rounded="lg">
      <span class="font-weight-medium">{{ snackbarText }}</span>
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false" :color="snackbarColor === 'warning' ? 'black' : 'white'" class="font-weight-bold">Ok</v-btn>
      </template>
    </v-snackbar>

    <v-overlay :value="loading" absolute class="rounded-lg">
      <v-progress-circular indeterminate size="48" color="#00E5FF" width="4" />
    </v-overlay>
  </v-container>
</template>

<script>
import { OneOnOnesService, UsersService, TeamsService, MicrosoftService, IntegrationsService } from '@/services'

export default {
  name: 'OneOnOneManager',

  data: () => ({
    loading: false,
    saving: false,
    microsoftConnected: false,

    // Filters
    selectedTeam: null,
    selectedUser: null,
    meetingSearch: '',
    showMeetingFilters: false,
    meetingTab: 0,

    // Data from API
    teams: [],
    allUsers: [],
    myTeam: [],
    availableMentors: [],
    teamMembers: [],
    teamStatus: {
      complianceRate: 0,
      completionPercent: 0,
      realized: 0,
      total: 0,
      pending: [],
    },
    stats: {
      scheduled: 0,
      completedThisMonth: 0,
      avgDuration: 0,
      streak: 0,
      totalMeetings: 0,
      completionRate: 0,
    },
    insightsGeneral: [],
    insightsForUser: [],
    frequentCollaborators: [],
    topDiscussedTopics: [],
    actionItemStats: {
      completed: 0,
      pending: 0,
    },
    meetings: [],

    // Occurred confirm dialog
    occurredConfirmDialog: false,
    meetingToMarkOccurred: null,
    markingOccurred: false,

    // Cancel dialog
    cancelDialog: false,
    meetingToCancel: null,
    cancelReason: '',
    cancelling: false,

    // Schedule dialog
    scheduleDialog: false,
    selectedMeetingToReschedule: null,
    validForm: false,
    showSuggestions: true,
    customTopic: '',
    suggestedTopics: [
      'Feedback e reconhecimento',
      'Desenvolvimento de carreira',
      'Desafios atuais',
      'Work-life balance',
      'Metas e objetivos',
      'Colaboração com o time',
      'Habilidades técnicas',
      'Processos e melhorias',
    ],
    newMeeting: {
      meetingType: 'mentor',
      userId: null,
      date: '',
      time: '14:00',
      duration: 30,
      selectedTopics: [],
      customTopics: [],
      notes: '',
    },
    defaultMeeting: {
      meetingType: 'mentor',
      userId: null,
      date: '',
      time: '14:00',
      duration: 30,
      selectedTopics: [],
      customTopics: [],
      notes: '',
    },
    timeOptions: [
      { text: '08:00', value: '08:00' }, { text: '08:30', value: '08:30' },
      { text: '09:00', value: '09:00' }, { text: '09:30', value: '09:30' },
      { text: '10:00', value: '10:00' }, { text: '10:30', value: '10:30' },
      { text: '11:00', value: '11:00' }, { text: '11:30', value: '11:30' },
      { text: '12:00', value: '12:00' }, { text: '13:00', value: '13:00' },
      { text: '13:30', value: '13:30' }, { text: '14:00', value: '14:00' },
      { text: '14:30', value: '14:30' }, { text: '15:00', value: '15:00' },
      { text: '15:30', value: '15:30' }, { text: '16:00', value: '16:00' },
      { text: '16:30', value: '16:30' }, { text: '17:00', value: '17:00' },
      { text: '17:30', value: '17:30' }, { text: '18:00', value: '18:00' },
    ],
    durationOptions: [
      { text: '15 min', value: 15 },
      { text: '30 min', value: 30 },
      { text: '45 min', value: 45 },
      { text: '60 min', value: 60 },
      { text: '90 min', value: 90 },
    ],

    // Notes dialog
    notesDialog: false,
    selectedMeeting: null,

    // Pauta preparation
    customPautaTopic: '',
    pautaSelectedTopics: [],
    pautaCustomTopics: [],
    showPautaSuggestions: false,

    // Snackbar
    snackbar: false,
    snackbarText: '',
    snackbarColor: 'success',
  }),

  computed: {
    isViewOnlyMode() {
      // Helper in case the application controls view permissions globally
      return false;
    },

    filteredUsers() {
      const currentUser = this.$store.getters.currentUser
      const currentUserId = currentUser?.id
      
      let users = this.selectedTeam ? this.teamMembers : this.allUsers
      
      users = users.filter(user => {
        if (user.role === 'ADMIN' || user.role === 'MANAGER') {
          return false
        }
        if (user.id === currentUserId) {
          return false
        }
        return true
      })
      
      return users
    },

    statsCards() {
      if (this.selectedUser) {
        const userMeetings = this.meetings.filter(m => m.userId === this.selectedUser)
        const completedUserMeetings = userMeetings.filter(m => m.status === 'COMPLETED')
        const thisMonthMeetings = completedUserMeetings.filter(m => {
          const d = new Date(m.scheduledAt)
          const now = new Date()
          return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear()
        })
        return [
          { label: 'Total de reuniões', value: userMeetings.length, icon: 'mdi-calendar-outline', iconColor: '#00E5FF' },
          { label: 'Este mês', value: thisMonthMeetings.length, icon: 'mdi-check-circle-outline', iconColor: '#34C759' },
          { label: 'Duração média', value: '30 min', icon: 'mdi-clock-outline', iconColor: '#00E5FF' },
          { label: 'Sequência', value: '2 semanas', icon: 'mdi-fire', iconColor: '#FF771C' },
        ]
      }
      return [
        { label: 'Agendadas', value: this.stats.scheduled, icon: 'mdi-calendar-clock', iconColor: '#00E5FF' },
        { label: 'Concluídas mês', value: this.stats.completedThisMonth, icon: 'mdi-check-circle-outline', iconColor: '#34C759' },
        { label: 'Duração média', value: (this.stats.avgDuration || 0) + ' min', icon: 'mdi-clock-outline', iconColor: '#00E5FF' },
        { label: 'Sequência', value: (this.stats.streak || 0) + ' sem.', icon: 'mdi-fire', iconColor: '#FF771C' },
        { label: 'Total de reuniões', value: this.stats.totalMeetings, icon: 'mdi-calendar-check-outline', iconColor: '#00E5FF' },
        { label: 'Taxa de conclusão', value: this.stats.completionRate + '%', icon: 'mdi-chart-line', iconColor: '#00E5FF' },
      ]
    },

    insights() {
      return this.selectedUser ? this.insightsForUser : this.insightsGeneral
    },

    insightsTitle() {
      if (this.selectedUser) {
        const user = this.allUsers.find(u => u.id === this.selectedUser)
        return user ? `Insights sobre ${user.name.split(' ')[0]}` : 'Insights'
      }
      return 'Insights'
    },

    maxCollabMeetings() {
      if (this.filteredCollaborators.length === 0) return 1
      return Math.max(...this.filteredCollaborators.map(c => c.totalMeetings))
    },

    filteredCollaborators() {
      if (this.selectedUser) {
        return this.frequentCollaborators.filter(collab => collab.userId === this.selectedUser || collab.id === this.selectedUser)
      }
      return this.frequentCollaborators
    },

    maxTopicCount() {
      if (this.topDiscussedTopics.length === 0) return 1
      return Math.max(...this.topDiscussedTopics.map(t => t.count))
    },

    isReschedulingMode() {
      return !!(this.selectedMeetingToReschedule && this.selectedMeetingToReschedule.id)
    },

    filteredUsersForMeeting() {
      if (this.isReschedulingMode) return this.allUsers
      if (this.newMeeting.meetingType === 'mentor') return this.myTeam
      else if (this.newMeeting.meetingType === 'mentorado') return this.availableMentors
      return this.allUsers
    },

    upcomingMeetings() {
      const now = new Date()
      return this.meetings
        .filter(m => m.scheduledAt && m.status !== 'CANCELLED' && new Date(m.scheduledAt) >= now)
        .sort((a, b) => new Date(a.scheduledAt) - new Date(b.scheduledAt))
    },

    pastMeetings() {
      const now = new Date()
      return this.meetings
        .filter(m => m.scheduledAt && new Date(m.scheduledAt) < now)
        .sort((a, b) => new Date(b.scheduledAt) - new Date(a.scheduledAt))
    },

    filteredUpcomingMeetings() {
      let result = this.upcomingMeetings
      if (this.selectedUser) result = result.filter(m => m.userId === this.selectedUser)
      if (this.selectedTeam) {
        const teamUserIds = this.allUsers.filter(u => u.teamId === this.selectedTeam).map(u => u.id)
        result = result.filter(m => teamUserIds.includes(m.userId))
      }
      if (this.meetingSearch) {
        const search = this.meetingSearch.toLowerCase()
        result = result.filter(m =>
          m.userName.toLowerCase().includes(search) ||
          (m.topics || []).some(t => t.toLowerCase().includes(search))
        )
      }
      return result
    },

    filteredPastMeetings() {
      let result = this.pastMeetings
      if (this.selectedUser) result = result.filter(m => m.userId === this.selectedUser)
      if (this.selectedTeam) {
        const teamUserIds = this.allUsers.filter(u => u.teamId === this.selectedTeam).map(u => u.id)
        result = result.filter(m => teamUserIds.includes(m.userId))
      }
      if (this.meetingSearch) {
        const search = this.meetingSearch.toLowerCase()
        result = result.filter(m =>
          m.userName.toLowerCase().includes(search) ||
          (m.topics || []).some(t => t.toLowerCase().includes(search))
        )
      }
      return result
    },
  },

  mounted() {
    this.loadAllData()
  },

  methods: {
    async loadAllData() {
      this.loading = true
      try {
        await Promise.all([
          this.loadMeetings(),
          this.loadTeamMembers(),
          this.loadMyTeam(),
          this.loadAvailableMentors(),
          this.loadStats(),
          this.loadTeamStatus(),
          this.loadInsights(),
          this.loadCollaborators(),
          this.loadTopics(),
          this.loadCompliance(),
          this.checkMicrosoftStatus(),
        ])
      } catch (e) {
        this.showSnackbar('Erro ao carregar dados do dashboard', 'error')
      } finally {
        this.loading = false
      }
    },
    
    async loadMeetings() {
      try {
        const response = await OneOnOnesService.list({ role: 'MANAGER' })
        this.meetings = response.data
      } catch (error) {
        this.showSnackbar('Erro ao carregar reuniões', 'error')
      }
    },

    async checkMicrosoftStatus() {
      try {
        const resp = await IntegrationsService.getMicrosoftStatus()
        this.microsoftConnected = !!(resp.data && resp.data.connected)
      } catch {
        this.microsoftConnected = false
      }
    },

    async loadTeamMembers() {
      try {
        const teamsResponse = await TeamsService.list()
        this.teams = teamsResponse.data || []
        const usersResponse = await UsersService.list()
        this.allUsers = usersResponse.data || []
      } catch (error) {
        this.showSnackbar('Erro ao carregar times e usuários', 'error')
      }
    },

    async loadMyTeam() {
      try {
        const response = await UsersService.getMyTeam()
        this.myTeam = response.data || []
      } catch (error) {}
    },

    async loadAvailableMentors() {
      try {
        const response = await UsersService.getAvailableMentors()
        this.availableMentors = response.data || []
      } catch (error) {}
    },

    async onTeamChange() {
      this.selectedUser = null
      this.teamMembers = []
      if (this.selectedTeam) {
        try {
          const response = await TeamsService.getMembers(this.selectedTeam)
          this.teamMembers = (response.data || []).map(member => ({
            ...member,
            id: member.id || member.userId
          }))
        } catch (error) {
          this.showSnackbar('Erro ao carregar membros do time', 'error')
        }
      }
      await this.onFilterChange()
    },

    async onFilterChange() {
      this.loading = true
      try {
        await Promise.all([
          this.loadStats(),
          this.loadTeamStatus(),
          this.loadInsights(),
          this.loadCollaborators(),
          this.loadTopics(),
          this.loadCompliance(),
        ])
      } catch (error) {} finally {
        this.loading = false
      }
    },

    async loadStats() {
      try {
        const params = {}
        if (this.selectedUser) params.userId = this.selectedUser
        if (this.selectedTeam) params.teamId = this.selectedTeam
        const response = await OneOnOnesService.getStats(params)
        this.stats = response.data
      } catch (e) {}
    },

    async loadTeamStatus() {
      try {
        const params = {}
        if (this.selectedTeam) params.teamId = this.selectedTeam
        const response = await OneOnOnesService.getTeamStatus(params)
        this.teamStatus = response.data
      } catch (e) {}
    },

    async loadInsights() {
      try {
        if (this.selectedUser) {
          const response = await OneOnOnesService.getInsights({ userId: this.selectedUser })
          this.insightsForUser = response.data.insightsList || []
        } else {
          const response = await OneOnOnesService.getInsights()
          this.insightsGeneral = response.data.insightsList || []
        }
      } catch (e) {}
    },

    async loadCollaborators() {
      try {
        const params = {}
        if (this.selectedUser) params.userId = this.selectedUser
        const response = await OneOnOnesService.getCollaborators(params)
        this.frequentCollaborators = response.data.sort((a, b) => b.totalMeetings - a.totalMeetings)
      } catch (e) {}
    },

    async loadTopics() {
      try {
        const params = {}
        if (this.selectedUser) params.userId = this.selectedUser
        const response = await OneOnOnesService.getTopics(params)
        this.topDiscussedTopics = response.data
      } catch (e) {}
    },

    async loadCompliance() {
      try {
        const response = await OneOnOnesService.getCompliance()
        if (response.data && typeof response.data.completed === 'number') {
          this.actionItemStats = response.data
        }
      } catch (e) {}
    },

    getTeamName(teamId) {
      const team = this.teams.find(t => t.id === teamId)
      return team ? team.name : ''
    },

    getSelectedUserData() {
      if (!this.selectedUser) return null
      return this.allUsers.find(u => u.id === this.selectedUser) || this.teamMembers.find(u => u.id === this.selectedUser)
    },

    getTeamComplianceRate(teamId) {
      if (this.teamStatus && this.selectedTeam === teamId) {
        return this.teamStatus.complianceRate
      }
      return null
    },

    openScheduleDialog(meeting = null) {
      this.selectedMeetingToReschedule = meeting
      this.newMeeting = { ...this.defaultMeeting, selectedTopics: [], customTopics: [] }
      this.customTopic = ''
      this.showSuggestions = true
      const tomorrow = new Date()
      tomorrow.setDate(tomorrow.getDate() + 1)
      this.newMeeting.date = tomorrow.toISOString().split('T')[0]
      this.newMeeting.time = '14:00'
      this.newMeeting.duration = 30
      if (meeting && meeting.userId) {
        this.newMeeting.userId = meeting.userId
      } else if (this.selectedUser) {
        this.newMeeting.userId = this.selectedUser
      }
      this.scheduleDialog = true
    },

    closeScheduleDialog() {
      this.selectedMeetingToReschedule = null
      try { if (this.$refs.form) this.$refs.form.resetValidation() } catch (e) {}
      this.customTopic = ''
      this.scheduleDialog = false
    },

    toggleTopic(topic) {
      const idx = this.newMeeting.selectedTopics.indexOf(topic)
      if (idx >= 0) this.newMeeting.selectedTopics.splice(idx, 1)
      else this.newMeeting.selectedTopics.push(topic)
    },

    addCustomTopic() {
      const topic = this.customTopic.trim()
      if (topic && !this.newMeeting.customTopics.includes(topic)) {
        this.newMeeting.customTopics.push(topic)
      }
      this.customTopic = ''
    },

    removeCustomTopic(index) {
      this.newMeeting.customTopics.splice(index, 1)
    },

    async scheduleMeeting() {
      if (!this.$refs.form.validate()) return

      this.saving = true
      try {
        const scheduledAt = `${this.newMeeting.date}T${this.newMeeting.time}:00`
        const allTopics = [...this.newMeeting.selectedTopics, ...this.newMeeting.customTopics]
        let savedMeeting = null

        if (this.selectedMeetingToReschedule && this.selectedMeetingToReschedule.id) {
          const meetingData = {
            ...this.selectedMeetingToReschedule,
            scheduledAt,
            status: 'SCHEDULED',
            topics: allTopics,
            notes: this.newMeeting.notes,
            duration: this.newMeeting.duration,
          }
          const resp = await OneOnOnesService.update(this.selectedMeetingToReschedule.id, meetingData)
          savedMeeting = resp.data || meetingData
          savedMeeting.id = this.selectedMeetingToReschedule.id

          if (this.microsoftConnected && this.selectedMeetingToReschedule.microsoftEventId) {
            try {
              const endTime = this.calcEndDateTime(scheduledAt, this.newMeeting.duration)
              await MicrosoftService.updateCalendarEvent(this.selectedMeetingToReschedule.microsoftEventId, {
                subject: '1:1 — ' + (this.selectedMeetingToReschedule.userName || 'Reunião'),
                start: { dateTime: scheduledAt, timeZone: 'America/Sao_Paulo' },
                end: { dateTime: endTime, timeZone: 'America/Sao_Paulo' },
              })
            } catch (msErr) {}
          }
          this.showSnackbar('Reunião reagendada com sucesso!', 'success')
        } else {
          const meetingData = {
            meetingType: this.newMeeting.meetingType,
            userId: this.newMeeting.userId,
            scheduledAt,
            status: 'SCHEDULED',
            topics: allTopics,
            notes: this.newMeeting.notes,
            duration: this.newMeeting.duration,
          }
          const resp = await OneOnOnesService.schedule(meetingData)
          savedMeeting = resp.data || meetingData

          if (this.microsoftConnected) {
            await this.createMicrosoftMeeting(savedMeeting, scheduledAt)
          }
          this.showSnackbar('Reunião agendada com sucesso!', 'success')
        }

        await this.loadMeetings()
        this.closeScheduleDialog()
      } catch (error) {
        this.showSnackbar('Erro ao agendar reunião', 'error')
      } finally {
        this.saving = false
      }
    },

    async createMicrosoftMeeting(savedMeeting, scheduledAt) {
      try {
        const selectedUser = this.allUsers.find(u => u.id === (savedMeeting.userId || this.newMeeting.userId))
        const userName = selectedUser?.name || selectedUser?.fullName || 'Colaborador'
        const currentUser = this.$store.getters.currentUser
        const managerName = currentUser?.name || currentUser?.fullName || 'Gestor'
        const subject = `1:1 — ${managerName} e ${userName}`
        const endDateTime = this.calcEndDateTime(scheduledAt, this.newMeeting.duration)

        const attendees = []
        if (selectedUser?.email) attendees.push(selectedUser.email)

        const eventResp = await MicrosoftService.createCalendarEvent({
          subject,
          startDateTime: scheduledAt,
          endDateTime,
          timeZone: 'America/Sao_Paulo',
          attendees,
          isOnlineMeeting: true,
        })

        const eventData = eventResp.data
        const meetingUrl = eventData.onlineMeeting?.joinUrl || null
        const microsoftEventId = eventData.id || null
        const outlookWebLink = eventData.webLink || null

        if (savedMeeting.id && (meetingUrl || microsoftEventId)) {
          try {
            await OneOnOnesService.updateMeetingLink(savedMeeting.id, {
              meetingUrl, microsoftEventId, outlookWebLink,
            })
          } catch (updateErr) {}
        }
      } catch (msErr) {
        this.showSnackbar('Reunião agendada, mas não foi possível criar link Teams.', 'warning')
      }
    },

    calcEndDateTime(startIso, durationMinutes) {
      const start = new Date(startIso)
      start.setMinutes(start.getMinutes() + (durationMinutes || 30))
      const pad = n => String(n).padStart(2, '0')
      return `${start.getFullYear()}-${pad(start.getMonth() + 1)}-${pad(start.getDate())}T${pad(start.getHours())}:${pad(start.getMinutes())}:00`
    },

    isMeetingToday(meeting) {
      if (!meeting.scheduledAt) return false
      const today = new Date()
      const date = new Date(meeting.scheduledAt)
      return (
        date.getFullYear() === today.getFullYear() &&
        date.getMonth() === today.getMonth() &&
        date.getDate() === today.getDate()
      )
    },

    openCancelDialog(meeting) {
      this.meetingToCancel = meeting
      this.cancelReason = ''
      this.cancelDialog = true
    },

    async confirmCancelMeeting() {
      if (!this.meetingToCancel) return
      this.cancelling = true
      try {
        await OneOnOnesService.cancel(this.meetingToCancel.id, this.cancelReason || undefined)

        if (this.microsoftConnected && this.meetingToCancel.microsoftEventId) {
          try { await MicrosoftService.deleteCalendarEvent(this.meetingToCancel.microsoftEventId) } catch (msErr) {}
        }

        this.meetings = this.meetings.filter(m => m.id !== this.meetingToCancel.id)
        this.cancelDialog = false
        this.meetingToCancel = null
        this.showSnackbar('Reunião cancelada com sucesso.', 'success')
      } catch (error) {
        this.showSnackbar('Erro ao cancelar reunião', 'error')
      } finally {
        this.cancelling = false
      }
    },

    openOccurredConfirmDialog(meeting) {
      this.meetingToMarkOccurred = meeting
      this.occurredConfirmDialog = true
    },

    markAsOccurred(meeting) {
      this.openOccurredConfirmDialog(meeting)
    },

    async confirmMarkAsOccurred() {
      if (!this.meetingToMarkOccurred) return
      this.markingOccurred = true
      try {
        await OneOnOnesService.markAsOccurred(this.meetingToMarkOccurred.id)
        const idx = this.meetings.findIndex(m => m.id === this.meetingToMarkOccurred.id)
        if (idx !== -1) {
          this.$set(this.meetings[idx], 'occurredAt', new Date().toISOString())
        }
        this.occurredConfirmDialog = false
        this.meetingToMarkOccurred = null
        this.showSnackbar('Reunião marcada como realizada!', 'success')
      } catch (error) {
        this.showSnackbar('Erro ao marcar reunião', 'error')
      } finally {
        this.markingOccurred = false
      }
    },

    startMeeting(meeting) {
      const meetingLink = meeting.meetingUrl || meeting.joinWebUrl
      if (meetingLink) {
        window.open(meetingLink, '_blank')
        this.showSnackbar('Abrindo reunião no Teams...', 'info')
      } else if (this.microsoftConnected) {
        this.createMeetingOnDemand(meeting)
      } else {
        this.showSnackbar('Link não disponível. Conecte o Microsoft no painel.', 'warning')
      }
    },

    async createMeetingOnDemand(meeting) {
      try {
        this.showSnackbar('Criando link Teams...', 'info')
        const endDateTime = this.calcEndDateTime(meeting.scheduledAt, meeting.duration || 30)
        const subject = `1:1 — ${meeting.userName || 'Reunião'}`

        const eventResp = await MicrosoftService.createCalendarEvent({
          subject, startDateTime: meeting.scheduledAt, endDateTime, timeZone: 'America/Sao_Paulo', attendees: [], isOnlineMeeting: true,
        })

        const meetingUrl = eventResp.data?.onlineMeeting?.joinUrl || null
        if (meetingUrl) {
          try { await OneOnOnesService.updateMeetingLink(meeting.id, { meetingUrl }) } catch (e) {}
          const idx = this.meetings.findIndex(m => m.id === meeting.id)
          if (idx !== -1) this.$set(this.meetings[idx], 'meetingUrl', meetingUrl)
          window.open(meetingUrl, '_blank')
        }
      } catch (err) {
        this.showSnackbar('Erro ao criar link Teams', 'error')
      }
    },

    preparePauta(meeting) {
      this.selectedMeeting = meeting
      const existingTopics = meeting.topics || []
      this.pautaSelectedTopics = existingTopics.filter(t => this.suggestedTopics.includes(t))
      this.pautaCustomTopics = existingTopics.filter(t => !this.suggestedTopics.includes(t))
      this.customPautaTopic = ''
      this.showPautaSuggestions = false
      this.notesDialog = true
    },

    togglePautaTopic(topic) {
      const index = this.pautaSelectedTopics.indexOf(topic)
      if (index > -1) this.pautaSelectedTopics.splice(index, 1)
      else this.pautaSelectedTopics.push(topic)
    },

    addCustomPautaTopic() {
      const topic = this.customPautaTopic.trim()
      if (topic && !this.pautaCustomTopics.includes(topic)) {
        this.pautaCustomTopics.push(topic)
        this.customPautaTopic = ''
      }
    },

    removeCustomPautaTopic(index) {
      this.pautaCustomTopics.splice(index, 1)
    },

    async savePautaTopics() {
      if (!this.selectedMeeting) return
      this.saving = true
      try {
        const allTopics = [...this.pautaSelectedTopics, ...this.pautaCustomTopics]
        await OneOnOnesService.updateTopics(this.selectedMeeting.id, allTopics)

        this.selectedMeeting.topics = allTopics
        const meetingIndex = this.meetings.findIndex(m => m.id === this.selectedMeeting.id)
        if (meetingIndex > -1) this.meetings[meetingIndex].topics = allTopics

        this.showSnackbar('Pauta salva com sucesso!', 'success')
        this.closeNotesDialog()
      } catch (error) {
        this.showSnackbar('Erro ao salvar pauta', 'error')
      } finally {
        this.saving = false
      }
    },

    closeNotesDialog() {
      this.notesDialog = false
      this.selectedMeeting = null
    },

    reschedule(meeting) {
      this.selectedMeetingToReschedule = meeting
      this.newMeeting = {
        meetingType: meeting.role === 'Mentorado' ? 'mentorado' : 'mentor',
        userId: meeting.userId,
        date: meeting.scheduledAt.split('T')[0],
        time: meeting.scheduledAt.split('T')[1].substring(0, 5),
        duration: meeting.duration || 30,
        selectedTopics: (meeting.topics || []).filter(t => this.suggestedTopics.includes(t)),
        customTopics: (meeting.topics || []).filter(t => !this.suggestedTopics.includes(t)),
        notes: meeting.notes || '',
      }
      this.showSuggestions = true
      this.customTopic = ''
      this.scheduleDialog = true
    },

    viewNotes(meeting) {
      this.selectedMeeting = meeting
      const existingTopics = meeting.topics || []
      this.pautaSelectedTopics = existingTopics.filter(t => this.suggestedTopics.includes(t))
      this.pautaCustomTopics = existingTopics.filter(t => !this.suggestedTopics.includes(t))
      this.customPautaTopic = ''
      this.showPautaSuggestions = false
      this.notesDialog = true
    },

    toggleMeetingExpand(meeting) {
      meeting.expanded = !meeting.expanded
    },

    handleInsightAction(insight) {
      if (insight.actionRoute) this.$router.push(insight.actionRoute)
      else this.showSnackbar('Em desenvolvimento', 'info')
    },

    formatMeetingDateShort(dateStr) {
      if (!dateStr) return '-'
      const date = new Date(dateStr)
      const days = ['dom.', 'seg.', 'ter.', 'qua.', 'qui.', 'sex.', 'sáb.']
      const months = ['jan.', 'fev.', 'mar.', 'abr.', 'mai.', 'jun.', 'jul.', 'ago.', 'set.', 'out.', 'nov.', 'dez.']
      return `${days[date.getDay()]}, ${date.getDate()} de ${months[date.getMonth()]}`
    },

    formatMeetingTime(dateStr) {
      if (!dateStr) return '-'
      const date = new Date(dateStr)
      return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    },

    formatMeetingDateFull(dateStr) {
      if (!dateStr) return '-'
      const date = new Date(dateStr)
      return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    },

    getInsightBgColor(type) {
      const isDark = this.$vuetify?.theme?.dark
      if (isDark) {
        const darkMap = { positive: '#00333D', info: '#2D1B33', warning: '#3E2E1A' }
        return darkMap[type] || '#2D2D2D'
      }
      const map = { positive: 'rgba(0, 229, 255, 0.1)', info: '#F3E5F5', warning: '#FFF3E0' }
      return map[type] || '#F5F5F5'
    },

    getInsightIconColor(type) {
      const map = { positive: '#00E5FF', info: '#7B1FA2', warning: '#F57C00' }
      return map[type] || '#757575'
    },

    getAvatarColor(name) {
      if (!name) return '#00E5FF'
      const colors = ['#00E5FF', '#2196F3', '#FF9800', '#9C27B0', '#00BCD4', '#E91E63', '#607D8B', '#795548']
      const index = name.charCodeAt(0) % colors.length
      return colors[index]
    },

    translateRole(role) {
      const map = { USER: 'Usuário', MANAGER: 'Gestor', TENANT_ADMIN: 'Admin', PLATFORM_OWNER: 'Owner', PARTNER_ADMIN: 'Parceiro' }
      return map[role] || role || ''
    },

    getInitials(name) {
      if (!name) return '?'
      const parts = name.split(' ')
      if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      return name.substring(0, 2).toUpperCase()
    },

    showSnackbar(text, color = 'success') {
      this.snackbarText = text
      this.snackbarColor = color
      this.snackbar = true
    },
  },
}
</script>

<style scoped>
.dashboard-container {
  max-width: 1440px;
  margin: 0 auto;
}

/* Custom Header */
.text-h5 {
  font-size: 1.5rem !important;
}
.text-h4 {
  font-family: 'Inter', 'Roboto', sans-serif !important;
  font-size: 1.5rem !important;
  font-weight: 600 !important;
  letter-spacing: -0.02em !important;
}

/* ========== Top Cards (Stats) ========== */
.top-cards-row {
  align-items: stretch;
}
.top-card {
  min-height: 140px;
}

/* ========== Base elevment-card structure ========== */
.elevment-card {
  border-radius: 12px !important;
  transition: transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
  border: 1px solid rgba(128, 128, 128, 0.15) !important;
}

.theme--dark .elevment-card { background-color: #1A1A1A !important; }
.theme--light .elevment-card { background-color: #FFFFFF !important; }

/* Divisores Sutis */
.theme-divider {
  border-color: rgba(128, 128, 128, 0.15) !important;
}

/* ========== Filters Section (Chips Globais Delineados) ========== */

/* Tags (Chips) do Vuetify */
::v-deep .v-chip {
  border-radius: 9999px !important;
  padding: 0 12px !important;
  font-weight: 600 !important;
  font-size: 0.75rem !important;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

/* Delineado Padrão para os Filtros do Topo */
::v-deep .theme-chip-filter {
  background-color: transparent !important;
  border: 1px solid rgba(128, 128, 128, 0.2) !important;
  color: rgba(128, 128, 128, 0.7) !important;
}
.theme--dark ::v-deep .theme-chip-filter { 
  border-color: rgba(255, 255, 255, 0.1) !important; 
  color: rgba(255, 255, 255, 0.6) !important; 
}

/* Filtro Ativo */
::v-deep .theme-chip-filter.active {
  border-color: #00E5FF !important;
  color: #00E5FF !important;
  background-color: rgba(0, 229, 255, 0.05) !important;
}

/* Tópicos Simples (Anotações) */
::v-deep .theme-chip-topic {
  background-color: transparent !important;
  border: 1px solid rgba(128, 128, 128, 0.2) !important;
  color: rgba(128, 128, 128, 0.7) !important;
  text-transform: none;
  font-weight: 500 !important;
  font-size: 0.7rem !important;
}
.theme--dark ::v-deep .theme-chip-topic { border-color: rgba(255, 255, 255, 0.1) !important; color: rgba(255, 255, 255, 0.6) !important; }

/* ========== Cores Delineadas Baseada nas Classes de Texto ========== */
::v-deep .v-chip.cyan--text { border-color: #00E5FF !important; color: #00E5FF !important; background-color: transparent !important; }
::v-deep .v-chip.success--text { border-color: #34C759 !important; color: #34C759 !important; background-color: transparent !important; }
::v-deep .v-chip.warning--text { border-color: #FF771C !important; color: #FF771C !important; background-color: transparent !important; }
::v-deep .v-chip.error--text { border-color: #FF3B30 !important; color: #FF3B30 !important; background-color: transparent !important; }
::v-deep .v-chip.purple--text { border-color: #9C27B0 !important; color: #9C27B0 !important; background-color: transparent !important; }
::v-deep .v-chip.grey--text { border-color: rgba(128, 128, 128, 0.3) !important; color: rgba(128, 128, 128, 0.7) !important; background-color: transparent !important; }
.theme--dark ::v-deep .v-chip.grey--text { border-color: rgba(255, 255, 255, 0.2) !important; color: rgba(255, 255, 255, 0.6) !important; }


/* ========== OVERRIDES DOS INPUTS (Filtros) ========== */
.theme--dark .elevment-input ::v-deep .v-input__control > .v-input__slot {
  background-color: #1A1A1A !important;
}
.theme--dark .elevment-input ::v-deep fieldset {
  border-color: rgba(255, 255, 255, 0.08) !important;
}
.theme--dark .elevment-input:hover ::v-deep fieldset {
  border-color: rgba(255, 255, 255, 0.2) !important;
}

.theme--light .elevment-input ::v-deep .v-input__control > .v-input__slot {
  background-color: #FFFFFF !important;
}
.theme--light .elevment-input ::v-deep fieldset {
  border-color: rgba(0, 0, 0, 0.12) !important;
}

/* ========== Menu List overrides ========== */
.theme--dark .elevment-menu {
  background-color: #1A1A1A !important;
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
}

/* ========== Meeting List Card visual enhancements ========== */
.meeting-list-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.1) !important;
}
.theme--light .meeting-list-card:hover {
  border-color: rgba(0, 0, 0, 0.15) !important;
}
.theme--dark .meeting-list-card:hover {
  border-color: rgba(255, 255, 255, 0.15) !important;
}

.meeting-card--past {
  opacity: 0.9;
}

.action-btn { letter-spacing: 0 !important; }
.action-btn-outlined {
  letter-spacing: 0 !important;
  border-color: rgba(128, 128, 128, 0.3) !important;
}

/* ========== Expanded Area ========== */
.expanded-area {
  border-radius: 0 0 12px 12px;
}


/* Dialogs */
.elevment-dialog-card {
  border-radius: 12px !important;
  border: 1px solid rgba(128, 128, 128, 0.15) !important;
}
.theme--dark .elevment-dialog-card { background-color: #1A1A1A !important; }
.theme--light .elevment-dialog-card { background-color: #FFFFFF !important; }

.elevment-box {
  background-color: rgba(128, 128, 128, 0.05);
  border-radius: 8px;
  border: 1px solid rgba(128, 128, 128, 0.1);
}

/* Custom Tabs Overlay */
.custom-tabs ::v-deep .v-tabs-bar {
  background-color: transparent !important;
}

/* Custom Scrollbar */
::-webkit-scrollbar { width: 6px; height: 6px; }
::-webkit-scrollbar-thumb { background: rgba(128, 128, 128, 0.3); border-radius: 3px; }
::-webkit-scrollbar-track { background: transparent; }

/* Animations */
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: translateY(0); }
}

.meeting-list-card,
.insight-item,
.top-card {
  animation: fadeIn 0.3s ease;
}
</style>